import {
  ExpandLess,
  ExpandMore,
  FeedSharp,
  HomeSharp,
  PeopleAltSharp,
  SettingsSharp,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Avatar,
  Collapse,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { CSSObject, styled, Theme, useTheme } from "@mui/material/styles";
import type {} from "@mui/material/themeCssVarsAugmentation";
import Toolbar from "@mui/material/Toolbar";
import type { JSX } from "react";
import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import AccessDenied from "../../components/AccessDenied";
import Authentication from "../../components/Authentication";
import CustomBreadcrumbs from "../../components/CustomBreadcrumbs";
import { TriggerProvider } from "../../contexts/TriggerContext";
import { viewImage } from "../../services/upload.service";
import { useAuthStore, useCurrentPageStore } from "../../stores/store";
import { AuthenStoreType } from "../../types/authen";
import { InitialUserPageType } from "../../types/global";

const drawerWidth = 272;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

type MenuType = {
  text: string;
  icon?: JSX.Element;
  to?: string;
  open?: boolean;
  children?: {
    text: string;
    icon?: JSX.Element;
    to: string;
  }[];
};

export default function Admin() {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const { user: auth, clearAuthState } = useAuthStore(
    (state) => state as AuthenStoreType,
  );
  const setCurrentPage = useCurrentPageStore(
    (state) => (state as InitialUserPageType).setCurrentPageState,
  );
  const { currentPage, clearCurrentPageState } = useCurrentPageStore(
    (state) => state as InitialUserPageType,
  );
  const [menus, setMenus] = useState<MenuType[]>([
    {
      text: "Dashboard",
      icon: <HomeSharp />,
      to: "/admin",
    },
    {
      text: "List ECP View",
      icon: <FeedSharp />,
      to: "/admin/ecp",
    },
    {
      text: "Setting",
      icon: <SettingsSharp />,
      open: false,
      children: [
        {
          text: "List Issue View",
          to: "/admin/setting/issue",
        },
        {
          text: "List Tag View",
          to: "/admin/setting/tag",
        },
        // {
        //   text: "List Tag Category View",
        //   to: "/admin/setting/tag-category",
        // },
      ],
    },
    {
      text: "Account Management",
      icon: <PeopleAltSharp />,
      open: false,
      children: [
        {
          text: "User Management",
          to: "/admin/account/user",
        },
        {
          text: "User Online",
          to: "/admin/account/user-online",
        },
        {
          text: "Activities",
          to: "/admin/account/activities",
        },
      ],
    },
  ]);

  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSignOut = () => {
    clearAuthState();
    clearCurrentPageState();
    navigate("/", { replace: true });
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      if (!isClosing) {
        if (mobileOpen) {
          const newMenus = menus.map((menu) => {
            if (menu?.open) {
              menu.open = false;
            }

            return { ...menu };
          });
          setMenus(newMenus);
        }
        setMobileOpen(!mobileOpen);
      }
    } else {
      if (open) {
        const newMenus = menus.map((menu) => {
          if (menu?.open) {
            menu.open = false;
          }

          return { ...menu };
        });
        setMenus(newMenus);
      }
      setOpen(!open);
    }
  };

  const handleClickMenu = (menu: MenuType) => {
    if (menu?.children && menu?.children?.length > 0) {
      if (mobileOpen || open) {
        const index = menus.findIndex((m) => m.text === menu.text);
        if (index !== -1) {
          const newMenus = [...menus];
          newMenus[index] = { ...newMenus[index], open: !newMenus[index].open };
          setMenus(newMenus);
        }
      } else {
        handleDrawerToggle();
      }
    } else {
      if (menu?.to) {
        if (isMobile) handleDrawerToggle();
        setCurrentPage(menu?.text ?? "");
        navigate(menu.to);
      }
    }
  };

  const drawer = (
    <List className="pt-[88px]">
      {menus.map((menu, index) => (
        <ListItem
          key={`menu-${index}`}
          disablePadding
          sx={{ display: "block" }}
        >
          <ListItemButton
            onClick={() => handleClickMenu(menu)}
            sx={[
              {
                minHeight: 48,
                px: 2,
              },
              open
                ? {
                    justifyContent: "initial",
                  }
                : {
                    justifyContent: "center",
                  },
            ]}
          >
            <ListItemIcon
              sx={[
                {
                  minWidth: 0,
                  justifyContent: "center",
                },
                isMobile
                  ? {
                      mr: 2,
                    }
                  : open
                    ? {
                        mr: 3,
                      }
                    : {
                        mr: "auto",
                      },
              ]}
            >
              {menu.icon}
            </ListItemIcon>
            <ListItemText
              primary={menu.text}
              // className="whitespace-break-spaces"
              slotProps={{
                primary: {
                  fontSize: theme.typography.body2.fontSize,
                },
              }}
              sx={[
                isMobile
                  ? {
                      display: "block",
                    }
                  : open
                    ? {
                        display: "block",
                      }
                    : {
                        display: "none",
                      },
              ]}
            />
            {(mobileOpen || open) &&
              menu?.children &&
              menu?.children?.length > 0 && (
                <>{menu?.open ? <ExpandLess /> : <ExpandMore />}</>
              )}
          </ListItemButton>
          {menu?.children && menu?.children?.length > 0 && (
            <Collapse in={menu?.open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {menu.children.map((child, childIdx) => (
                  <ListItemButton
                    key={`child-menu-${childIdx}`}
                    onClick={() => handleClickMenu(child)}
                    sx={{ pl: 8 }}
                  >
                    {child?.icon && <ListItemIcon>{child.icon}</ListItemIcon>}
                    <ListItemText
                      primary={child?.text}
                      // className="whitespace-break-spaces"
                      slotProps={{
                        primary: {
                          fontSize: theme.typography.body2.fontSize,
                        },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          )}
        </ListItem>
      ))}
    </List>
  );

  return (
    <Authentication>
      <TriggerProvider>
        <Box className="flex">
          <AppBar
            position="fixed"
            open={open}
            className="flex justify-center h-16"
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                edge="start"
                sx={[
                  {
                    mr: 2,
                  },
                ]}
              >
                <MenuIcon className="text-white" />
              </IconButton>
              <div className="w-[41px] h-[41px] flex-grow flex items-center font-bold">
                {/* <img
                src="/images/logo-essilor-luxottica.svg"
                className="w-[41px] h-[41px]"
                alt="essilor-logo"
              /> */}
                Essilor Luxottica
              </div>
              {auth && (
                <div>
                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    color="inherit"
                  >
                    <Avatar
                      src={auth?.image ? viewImage(auth?.image) : undefined}
                    />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: 272,
                        },
                      },
                    }}
                  >
                    <MenuItem>
                      <Stack
                        direction="row"
                        spacing={4}
                        useFlexGap
                        sx={{
                          alignItems: "center",
                        }}
                      >
                        <Avatar
                          src={auth?.image ? viewImage(auth?.image) : undefined}
                        />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body1">{auth?.name}</Typography>
                          <Typography
                            variant="body2"
                            color="grey.400"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {auth?.role}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => navigate("/admin/profile-settings")}
                    >
                      <Typography variant="body2">Profile Settings</Typography>
                    </MenuItem>
                    {/* <MenuItem onClick={handleClose}>
                    <Typography variant="body2">
                      Transaction Trancate Data
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={handleClose}>
                    <Typography variant="body2">Backup Database</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleClose}>
                    <Typography variant="body2">Export to Excel</Typography>
                  </MenuItem> */}
                    <MenuItem onClick={() => onSignOut()}>
                      <Typography variant="body2">Sign Out</Typography>
                    </MenuItem>
                  </Menu>
                </div>
              )}
            </Toolbar>
          </AppBar>
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            onTransitionEnd={handleDrawerTransitionEnd}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiPaper-root": {
                paddingTop: "88px",
              },
            }}
          >
            {drawer}
          </MuiDrawer>
          <Drawer
            variant="permanent"
            open={open}
            sx={{
              display: { xs: "none", md: "block" },
            }}
          >
            {drawer}
          </Drawer>
          <Box component="main" className="flex-grow py-6 px-8">
            <DrawerHeader />
            <div className="container mx-auto">
              <CustomBreadcrumbs className="mb-2" />
              {auth?.role !== "admin" ? <AccessDenied /> : <Outlet />}
            </div>
          </Box>
        </Box>
      </TriggerProvider>
    </Authentication>
  );
}
